import { FC, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { initLightboxJS } from 'lightbox.js-react';
import routes from 'routes';
import Loadable from 'components/Loadable/Loadable';
import PrivateRoute from 'components/PrivateRoute';
import { WithPermission } from 'components/PermissionRoute/WithPermission';
import ScrollToTop from 'components/ScrollToTop';
import PasswordRecovery from 'pages/Authentication/PasswordRecovery';
import PasswordReset from 'pages/Authentication/PasswordReset';
import PasswordVerify from 'pages/Authentication/PasswordVerify';
import SignIn from 'pages/Authentication/SignIn';
import AppLayout from 'layouts/AppLayout';

const Migration = Loadable(lazy(() => import('pages/Migration/List')));
const ViewPart = Loadable(lazy(() => import('pages/Migration/ViewPart')));
const ViewVehicle = Loadable(lazy(() => import('pages/Migration/ViewVehicle')));
const PartTypes = Loadable(lazy(() => import('pages/PartTypes/List')));
const ViewPartType = Loadable(lazy(() => import('pages/PartTypes/View')));
const NewPartType = Loadable(lazy(() => import('pages/PartTypes/New/index')));
const EditPartType = Loadable(lazy(() => import('pages/PartTypes/Edit/index')));
const PartTypeBulkEdit = Loadable(lazy(() => import('pages/PartTypes/BulkEdit/index')));
const AddPartAttribute = Loadable(lazy(() => import('pages/PartTypes/AddPartAttribute')));
const EditPartAttribute = Loadable(lazy(() => import('pages/PartTypes/EditPartAttribute')));
const VehicleData = Loadable(lazy(() => import('pages/VehicleData')));
const CategoryManagement = Loadable(lazy(() => import('pages/CategoryManagement')));
const PartsCatalog = Loadable(lazy(() => import('pages/PartsCatalog/Search')));
const PartsCatalogAdd = Loadable(lazy(() => import('pages/PartsCatalog/Add')));
const PartsCatalogEdit = Loadable(lazy(() => import('pages/PartsCatalog/Edit')));
const PartsDecoder = Loadable(lazy(() => import('pages/PartsDecoder/List')));
const PartsDecoderView = Loadable(lazy(() => import('pages/PartsDecoder/View')));
const OfficeHours = Loadable(lazy(() => import('pages/OfficeHours/List')));
const SystemNotices = Loadable(lazy(() => import('pages/SystemNotices')));
const Releases = Loadable(lazy(() => import('pages/Releases/List')));
const ReleaseForm = Loadable(lazy(() => import('pages/Releases/Form')));
const HtmlsPage = Loadable(lazy(() => import('pages/Htmls')));
const LabelsPage = Loadable(lazy(() => import('pages/Labels')));
const LearnContentList = Loadable(lazy(() => import('pages/LearnContent/List')));
const LearnContentForm = Loadable(lazy(() => import('pages/LearnContent/Form')));
const FeedbackManagementPage = Loadable(lazy(() => import('pages/FeedbackManagement/List')));
const UsersListPage = Loadable(lazy(() => import('pages/Users/List')));
const UsersViewPage = Loadable(lazy(() => import('pages/Users/View')));
const UsersAddPage = Loadable(lazy(() => import('pages/Users/Add')));
const UsersEditPage = Loadable(lazy(() => import('pages/Users/Edit')));
const RecordDiagnosticsPage = Loadable(lazy(() => import('pages/RecordDiagnostics')));
const NotAuthorizedPage = Loadable(lazy(() => import('pages/NotAuthorized')));

const SaturnMigrationPage = Loadable(lazy(() => import('pages/SaturnMigration/List')));
const SaturnMigrationView = Loadable(lazy(() => import('pages/SaturnMigration/View')));

const PartTypeStudioPage = Loadable(lazy(() => import('pages/PartTypeStudio')));

const DPCManagementPage = Loadable(lazy(() => import('pages/DPCManagement/List')));

const StoresListPage = Loadable(lazy(() => import('pages/Stores/List')));
const StoreNewPage = Loadable(lazy(() => import('pages/Stores/New')));
const StoreEditorPage = Loadable(lazy(() => import('pages/Stores/Editor')));
const StoreBuildPage = Loadable(lazy(() => import('pages/Stores/Build')));

const DemosGallery = Loadable(lazy(() => import('pages/Demos')));
const PartPricingDemoPage = Loadable(lazy(() => import('pages/Demos/PartPricing')));
const AuctionValuationDemoPage = Loadable(lazy(() => import('pages/Demos/AuctionValuation')));
const PartMatchingDemoPage = Loadable(lazy(() => import('pages/Demos/PartMatching')));

const App: FC = () => {
  useEffect(() => {
    initLightboxJS('35E3-6C20-1A44-34C5', 'individual');
  }, []);

  return (
    <ScrollToTop>
      <Routes>
        {/* AI agent demos — public + chrome-free, so they can be shared without login */}
        <Route path={routes.demos.index} element={<DemosGallery />} />
        <Route path={routes.demos.partPricing} element={<PartPricingDemoPage />} />
        <Route path={routes.demos.auctionValuation} element={<AuctionValuationDemoPage />} />
        <Route path={routes.demos.partMatching} element={<PartMatchingDemoPage />} />

        <Route path={routes.auth.logIn} element={<SignIn />} />
        <Route path={routes.auth.passwordRecovery} element={<PasswordRecovery />} />
        <Route path={routes.auth.passwordReset} element={<PasswordReset />} />
        <Route path={routes.auth.verifyCode} element={<PasswordVerify />} />

        <Route element={<PrivateRoute />}>
          <Route path={routes.home} element={<AppLayout />}>
            {/* Land authenticated users on Part Types instead of the empty layout at "/" */}
            <Route index element={<Navigate to={routes.partTypes.list} replace />} />
            <Route path={routes.notAuthorized} element={<NotAuthorizedPage />} />

            <Route path={routes.DPCManagement.list} element={WithPermission('dpc-management', <DPCManagementPage />)} />

            <Route path={routes.saturnMigration.list} element={WithPermission('saturn-migration', <SaturnMigrationPage />)} />
            <Route path={routes.saturnMigration.view} element={WithPermission('saturn-migration', <SaturnMigrationView />)} />

            <Route path={routes.migration.list} element={WithPermission('migration', <Migration />)} />
            <Route path={routes.migration.viewPart} element={WithPermission('migration', <ViewPart />)} />
            <Route path={routes.migration.viewVehicle} element={WithPermission('migration', <ViewVehicle />)} />
            <Route path={routes.partTypes.list} element={WithPermission('part-types', <PartTypes />)} />
            <Route path={routes.partTypes.new} element={WithPermission('part-types', <NewPartType />)} />
            <Route path={routes.partTypes.view} element={WithPermission('part-types', <ViewPartType />)} />
            <Route path={routes.partTypes.edit} element={WithPermission('part-types', <EditPartType />)} />
            <Route path={routes.partTypes.bulkEdit} element={WithPermission('part-types', <PartTypeBulkEdit />)} />
            <Route path={routes.partTypes.addAttribute} element={WithPermission('part-types', <AddPartAttribute />)} />
            <Route path={routes.partTypes.editAttribute} element={WithPermission('part-types', <EditPartAttribute />)} />
            <Route path={routes.partTypeStudio.all} element={WithPermission('part-type-studio', <PartTypeStudioPage />)} />
            <Route path={routes.vehicleData.list} element={WithPermission('vehicle-data', <VehicleData />)} />
            <Route
              path={routes.categoryManagement.list}
              element={WithPermission('category_management', <CategoryManagement />)}
            />
            <Route path={routes.partsCatalog.list} element={WithPermission('parts-catalog', <PartsCatalog />)} />
            <Route path={routes.partsCatalog.add} element={WithPermission('parts-catalog', <PartsCatalogAdd />)} />
            <Route path={routes.partsCatalog.edit} element={WithPermission('parts-catalog', <PartsCatalogEdit />)} />
            <Route path={routes.partsDecoder.list} element={WithPermission('parts-decoder', <PartsDecoder />)} />
            <Route path={routes.partsDecoder.view} element={WithPermission('parts-decoder', <PartsDecoderView />)} />
            <Route path={routes.officeHours.list} element={WithPermission('office-hours', <OfficeHours />)} />
            <Route path={routes.systemNotices.list} element={WithPermission('system-notices', <SystemNotices />)} />
            <Route path={routes.releases.list} element={WithPermission('releases', <Releases />)} />
            <Route path={routes.releases.create} element={WithPermission('releases', <ReleaseForm />)} />
            <Route path={routes.releases.edit} element={WithPermission('releases', <ReleaseForm />)} />
            <Route path={routes.htmls.list} element={WithPermission('ebay-html', <HtmlsPage />)} />
            <Route path={routes.labels.list} element={WithPermission('ebay-html', <LabelsPage />)} />
            <Route path={routes.learnContent.list} element={WithPermission('learn-content', <LearnContentList />)} />
            <Route path={routes.learnContent.create} element={WithPermission('learn-content', <LearnContentForm />)} />
            <Route path={routes.learnContent.edit} element={WithPermission('learn-content', <LearnContentForm />)} />
            <Route path={routes.feedbackManagement.list} element={WithPermission('feedback-management', <FeedbackManagementPage />)} />
            <Route path={routes.users.list} element={WithPermission('users', <UsersListPage />)} />
            <Route path={routes.users.view} element={WithPermission('users', <UsersViewPage />)} />
            <Route path={routes.users.add} element={WithPermission('users', <UsersAddPage />)} />
            <Route path={routes.users.edit} element={WithPermission('users', <UsersEditPage />)} />
            <Route path={routes.recordDiagnostics.list} element={WithPermission('record-diagnostics', <RecordDiagnosticsPage />)} />
            <Route path={routes.stores.list} element={WithPermission('stores', <StoresListPage />)} />
            <Route path={routes.stores.new} element={WithPermission('stores', <StoreNewPage />)} />
            <Route path={routes.stores.edit} element={WithPermission('stores', <StoreEditorPage />)} />
            <Route path={routes.stores.build} element={WithPermission('stores', <StoreBuildPage />)} />
            <Route path='*' element={<Navigate to={routes.partTypes.list} replace />} />
          </Route>
        </Route>
      </Routes>
    </ScrollToTop>
  );
};

export default App;
